import { useQuery } from '@tanstack/react-query';
import { requestsApi } from '../requests/api.ts';
import { InvitesPanel } from '../../components/InvitesPanel.tsx';
import { ComparisonTable } from '../../components/ComparisonTable.tsx';
import { ManualQuotePanel } from '../../components/ManualQuotePanel.tsx';
import { ModeChooser } from '../../components/ModeChooser.tsx';
import { SpecDocBox } from '../../components/SpecDocBox.tsx';
import { CountdownBadge } from '../../components/CountdownBadge.tsx';
import { t } from '../../lib/strings.ts';
import { formatDate } from '../../lib/format.ts';

interface Props {
  requestId: string;
}

export function RequestPanel({ requestId }: Props): JSX.Element {
  const { data: request, isLoading } = useQuery({
    queryKey: ['request', requestId],
    queryFn: () => requestsApi.get(requestId),
    refetchOnWindowFocus: true,
  });

  if (isLoading) {
    return <p className="status-label text-muted-foreground" role="status">{t.requestDetail.loading}</p>;
  }

  if (!request) {
    return <p className="text-sm text-destructive">{t.requestDetail.notFound}</p>;
  }

  return (
    <div className="space-y-6">
      {/* Request header info */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span className="mono-meta">{request.refNumber}</span>
        <span>{t.requestDetail.createdAt} <span className="num">{formatDate(request.createdAt)}</span></span>
        {request.deadline && (
          <span>{t.requestDetail.deadlineLabel}: <span className="num">{formatDate(request.deadline)}</span></span>
        )}
        {request.status === 'SENT' && <CountdownBadge deadline={request.deadline} />}
      </div>

      {/* Items table */}
      <section className="space-y-2">
        <div className="section-heading">
          <h3 className="status-label text-muted-foreground">
            {t.requestDetail.itemsHeading} (<span className="num">{request.items.length}</span>)
          </h3>
        </div>

        {/* Mobile: stacked cards */}
        <ul className="space-y-2 md:hidden">
          {request.items.map((item, idx) => (
            <li key={item.id} className="card-paper p-3.5">
              <div className="flex items-start justify-between gap-3">
                <p className="font-medium leading-snug">{item.name}</p>
                <span className="mono-meta text-muted-foreground shrink-0 num">#{idx + 1}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span><span className="tabular text-foreground num">{item.qty}</span> {item.unit}</span>
                {item.spec && <span>{item.spec}</span>}
              </div>
            </li>
          ))}
        </ul>

        {/* Desktop: table */}
        <div className="card-paper hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colNum}</th>
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colItem}</th>
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colQty}</th>
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colUnit}</th>
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colSpec}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {request.items.map((item, idx) => (
                <tr key={item.id} className="hover:bg-muted/40 transition-colors">
                  <td className="px-4 py-3 mono-meta text-muted-foreground num">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium">{item.name}</td>
                  <td className="px-4 py-3 tabular num">{item.qty}</td>
                  <td className="px-4 py-3 text-muted-foreground">{item.unit}</td>
                  <td className="px-4 py-3 text-muted-foreground">{item.spec ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Mode chooser — procurement staff, fresh draft only */}
      <ModeChooser requestId={requestId} mode={request.mode} status={request.status} />

      {request.mode === 'MANUAL' ? (
        /* Manual quotation flow */
        <section aria-label={t.manualQuote.heading}>
          <ManualQuotePanel requestId={requestId} />
        </section>
      ) : (
        <>
          {/* Optional specification document for vendors */}
          <SpecDocBox requestId={requestId} />

          {/* Vendor Invitations panel */}
          <section aria-label={t.requestDetail.ariaVendorInvitations}>
            <InvitesPanel requestId={requestId} deadline={request.deadline} />
          </section>

          {/* Quote Comparison + Award panel (shown once invites sent) */}
          {request.status !== 'DRAFT' && (
            <section aria-label={t.requestDetail.ariaQuoteComparison}>
              <ComparisonTable requestId={requestId} />
            </section>
          )}
        </>
      )}
    </div>
  );
}
